import {
  Button,
  Input,
  InputGroup,
  InputRightElement,
  Stack,
  useDisclosure,
} from '@chakra-ui/react';
import useOrder from '@hooks/useOrder';
import { orderSelector, TOrderInitialState } from '@stores/order';
import { ChangeEvent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaSquareParking } from 'react-icons/fa6';
import { PiMoneyBold } from 'react-icons/pi';
import { TbLetterASmall } from 'react-icons/tb';
import { useSelector } from 'react-redux';
import { allowOnlyNumber, setComma, setCurrency } from 'utils/common';
import { fetchUserMoney } from '../queries';

type TDiscountSectionProps = {
  icon: JSX.Element;
  title: string;
  currentValue: number | undefined;
  maxAmount: number;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onApply: () => void;
  label: string;
  addPoints?: (points: number) => void;
};

const calculateDiscount = (total: number, target: number, compare = 0) => {
  return Math.max(0, Math.min(total - compare, target));
};

export default function CalculateDiscount() {
  const { data } = fetchUserMoney();
  const money = data?.money || { cash: 0, point: 0 };
  const { useCash = 0, usePoint = 0, coupon } = useSelector(orderSelector);
  const { isOpen, onClose } = useDisclosure({ defaultIsOpen: money.point > 0 });
  const {
    order: { cart },
    updateOrderInfo,
    productAmount,
    deliveryFee,
    hotdealCount,
    promotionDiscount,
    promotionConstant,
  } = useOrder();
  const { t } = useTranslation('order');

  const tp =
    productAmount +
    deliveryFee -
    ((cart?.store?.isAllHotdeal === 2 ? 1000 : hotdealCount * 1000) +
      promotionDiscount +
      promotionConstant +
      (coupon?.amount || 0));

  const initialCashUsage = calculateDiscount(tp, money.cash);

  const handleAllUseCash = () => {
    if (money.cash) {
      let totalCash = calculateDiscount(tp, money.cash, usePoint || 0);
      let remainingTotal = Math.max(0, tp - totalCash);

      const totalPoint = calculateDiscount(remainingTotal, money.point);
      remainingTotal -= totalPoint;

      if (totalCash > tp) {
        totalCash = Math.min(money.cash, tp);
      }

      updateOrderInfo({ useCash: totalCash });
    }
  };

  const handleAllUsePoint = () => {
    if (money.point) {
      const [_usePoint, _useCash] = calculatePointUsage(money.point);

      updateOrderInfo({ usePoint: _usePoint, useCash: _useCash });
      onClose();
    }
  };

  const calculatePointUsage = (points: number) => {
    const maxPoints = money.point;
    const usablePoints = Math.min(points, maxPoints, tp);
    let usableCash = initialCashUsage;

    if (usablePoints + useCash > tp) {
      usableCash = Math.max(0, tp - usablePoints);
    } else {
      usableCash = Math.max(0, initialCashUsage - usablePoints, useCash);
    }
    return [usablePoints, usableCash];
  };

  const handleCashChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = castToNumber(e.target.value);
    const cashUsage = calculateDiscount(
      tp,
      Math.min(value, money.cash),
      usePoint,
    );
    updateOrderInfo({ useCash: cashUsage });
  };

  const handlePointChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = Math.min(castToNumber(e.target.value), money.point);
    const [_usePoint, _useCash] = calculatePointUsage(value);
    updateOrderInfo({ usePoint: _usePoint, useCash: _useCash });
  };

  const castToNumber = (value: string) =>
    allowOnlyNumber(value.startsWith('0') ? value.slice(1) : value);

  const addPoints = (additionalPoints: number) => {
    const pointsToAdd = Math.min(
      money.point - (usePoint || 0),
      additionalPoints,
    );
    const [_usePoint, _useCash] = calculatePointUsage(
      (usePoint || 0) + pointsToAdd,
    );
    updateOrderInfo({ usePoint: _usePoint, useCash: _useCash });
  };

  useEffect(() => {
    handleAllUseCash();
  }, [tp, money.cash]);

  return (
    <section className="my-4 flex flex-col gap-4">
      <Stack spacing="20px">
        <DiscountSection
          icon={<PiMoneyBold />}
          title="Panther Cash"
          currentValue={useCash}
          maxAmount={money.cash}
          onChange={handleCashChange}
          onApply={handleAllUseCash}
          label={t('payment:available_panther_cash_balance')}
        />
        <DiscountSection
          icon={<FaSquareParking />}
          title="Panther Point"
          currentValue={usePoint}
          maxAmount={money.point}
          onChange={handlePointChange}
          onApply={handleAllUsePoint}
          label={t('payment:available_panther_points_balance')}
          addPoints={addPoints}
        />
      </Stack>
    </section>
  );
}

function DiscountSection({
  icon,
  title,
  currentValue = 0,
  maxAmount,
  onChange,
  onApply,
  label,
  addPoints,
}: TDiscountSectionProps) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <span className="font-bold text-lg">{title}</span>
      </div>
      <p>
        {label}: {setCurrency('kr', setComma(maxAmount))}
      </p>
      <InputGroup>
        <Input
          value={setComma(currentValue || 0)}
          onChange={onChange}
          onFocus={(e) => {
            if (e.target.value === '0') e.target.value = '';
          }}
          variant="filled"
        />
        <InputRightElement
          as="button"
          type="button"
          onClick={onApply}
          className="bg-primary-300 rounded-full"
        >
          <TbLetterASmall size="32px" color="white" />
        </InputRightElement>
      </InputGroup>
      {addPoints && (
        <div className="my-4 flex gap-2 justify-end">
          {[1000, 500, 100, 10].map((points) => (
            <Button
              key={points}
              size="sm"
              isDisabled={maxAmount - currentValue < points}
              onClick={() => addPoints(points)}
            >
              +{points}P
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}
